import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

import { usePageStore } from './page'
import { useConfigStore } from './config'
import {
  getConfig,
  greaterThanNow,
  setCookie
} from './utils/helpers'
import {
  facebookLogin as facebookLoginOrcaApi,
  facebookRegister as facebookRegisterOrcApi,
  getOneTimePassword as getOneTimePasswordOrcApi,
  login as loginOrcApi,
  refreshAuthToken as refreshAuthTokenOrcApi,
  activate,
  changePassword as changePasswordOrcApi,
  changePasswordOtp as changePasswordOtpOrcApi,
  checkDisplayNameExists as checkDisplayNameExistsOrcApi,
  getUserProfile,
  register,
  resetPassword as resetPasswordOrcApi,
  deleteUser as deleteUserOrcApi
} from './utils/orc-api'

import type { CaptchaAnswer } from './utils/orc-api'
import type { ProfileAsset } from '@xcel-next/types'

const REFRESHING_INTERVAL_SECONDS = 600
const CHECK_COOKIE_INTERVAL_SECONDS = 3

let refreshTokenInterval: ReturnType<typeof setInterval> | undefined
let checkCookieInterval: ReturnType<typeof setInterval> | undefined
let localStorageListenerAdded = false

type JwtPayload = {
  exp: number,
  userId?: string,
  hasPurSub?: boolean,
  accounts?: string[],
  [key: string]: unknown
}

type UserData = JwtPayload & {
  oneTimePassword?: string
}

type LoginCredentials = {
  username: string,
  password: string
}

type FacebookCredentials = {
  accessToken: string,
  facebookId: string
}

type RegisterPayload = {
  email: string,
  password: string,
  acceptedTermsAndConditions: boolean,
  acceptedDataPrivacyPolicy: boolean,
  newsletter?: boolean,
  captcha?: CaptchaAnswer
}

type EnsureLoginOptions = {
  onSuccess?: () => void,
  onAbort?: () => void,
  hasTokenExpired?: boolean
}

function isJwtCookieSet (): boolean {
  return !import.meta.env.SSR && document.cookie.includes('Authorization=')
}

function clearJwtCookie (): void {
  if (!import.meta.env.SSR) {
    setCookie('Authorization', '', new Date())
    localStorage.removeItem('isLoggedIn')
  }
}

function setJwtCookie (jwt: string, expDate: Date): void {
  if (!import.meta.env.SSR) {
    setCookie('Authorization', jwt, expDate)
    localStorage.setItem('isLoggedIn', '1')
  }
}

function parseJwt (token: string): JwtPayload {
  const base64Url = token.split('.')[1]
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
  const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
    return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
  }).join(''))

  return JSON.parse(jsonPayload)
}

function tokenIsValid (token: string | null): boolean {
  if (!token) {
    return false
  }
  try {
    const jwt = parseJwt(token)
    if (!greaterThanNow(jwt.exp * 1000)) {
      return false
    }
  } catch (_e) {
    return false
  }

  return true
}

export const useSessionStore = defineStore('session', () => {
  // Stores
  const pageStore = usePageStore()
  const configStore = useConfigStore()

  // Centralise the repeated getConfig call to avoid spreading ???? all over the place
  const orcConfig = () => getConfig(configStore.rsConfig.orchestrationServiceUrl ?? '', token.value ?? undefined)

  // State
  const token = ref<string | null>(null)
  const user = ref<UserData | null>(null)
  const profile = ref<ProfileAsset | null>(null)
  const loginOverlay = ref<unknown>(null)

  // Getters
  const isLoggedIn = computed(() => !!token.value)

  const hasPurSub = computed(() => {
    return !!user.value?.hasPurSub
  })

  // Actions
  const storeToken = (newToken: string | null) => {
    token.value = newToken
  }

  const storeUser = (newUser: UserData | null) => {
    user.value = newUser
  }

  const storeProfile = (newProfile: ProfileAsset | null) => {
    profile.value = newProfile
  }

  const clearLoginOverlay = () => {
    loginOverlay.value = null
  }

  const storeLoginOverlay = (data: unknown) => {
    loginOverlay.value = data
  }

  const storeOneTimePassword = (oneTimePassword: string) => {
    if (user.value) {
      user.value.oneTimePassword = oneTimePassword
    }
  }

  const login = async (credentials: LoginCredentials) => {
    const data = await loginOrcApi(orcConfig(), credentials.username, credentials.password, pageStore.brandFromStore)
    if (data) {
      setToken(data)
      await userInit()
    }
  }

  const facebookLogin = async (credentials: FacebookCredentials) => {
    const data = await facebookLoginOrcaApi(orcConfig(), credentials.accessToken, credentials.facebookId, pageStore.brandFromStore)
    if (data) {
      setToken(data)
      await userInit()
    }
  }

  const facebookRegister = async (credentials: FacebookCredentials) => {
    const data = await facebookRegisterOrcApi(orcConfig(), credentials.accessToken, credentials.facebookId, pageStore.brandFromStore)
    if (data) {
      setToken(data)
      await userInit()
    }
  }

  const logout = () => {
    clearUserToken()
  }

  const getOneTimePassword = async (): Promise<string | undefined> => {
    try {
      if (user.value?.accounts?.includes('fb')) {
        return getOneTimePasswordOrcApi(orcConfig(), user.value.userId, pageStore.brandFromStore)
      }
    } catch (err) {
      console.warn('Error while getting otp:', err)
    }
    return undefined
  }

  const forceLogout = () => {
    clearUserToken()
    window.location.href = '/?notify=logout-success'
  }

  const ensureLogin = ({ onSuccess, onAbort, hasTokenExpired }: EnsureLoginOptions = {}) => {
    if (token.value && token.value.length > 0) {
      return onSuccess?.()
    }

    storeLoginOverlay({
      hasTokenExpired,
      success: () => {
        clearLoginOverlay()
        onSuccess?.()
      },
      abort: () => {
        clearLoginOverlay()
        forceLogout()
        onAbort?.()
      }
    })
  }

  const setToken = (newToken: string) => {
    try {
      const jwt = parseJwt(newToken)
      if (!greaterThanNow(jwt.exp * 1000)) {
        return clearUserToken()
      }
      storeToken(newToken)
      storeUser(jwt)
      setJwtCookie(newToken, new Date(jwt.exp * 1000))
    } catch (error) {
      console.error('error setting token', error)
      clearUserToken()
    }
  }

  const startSessionIntervals = () => {
    refreshTokenInterval ??= setInterval(() => refreshAuthToken(), REFRESHING_INTERVAL_SECONDS * 1000)
    checkCookieInterval ??= setInterval(() => checkCookie(), CHECK_COOKIE_INTERVAL_SECONDS * 1000)
  }

  const refreshAuthToken = async () => {
    try {
      if (!tokenIsValid(token.value)) {
        return
      }
      setToken(await refreshAuthTokenOrcApi(orcConfig()))
    } catch (e) {
      console.error('Error while refreshing user token', e)
    }
  }

  const checkCookie = async () => {
    if (!isJwtCookieSet()) {
      clearUserToken()
      return ensureLogin({ hasTokenExpired: true })
    }
  }

  const watchLocalStorage = async () => {
    if (!import.meta.env.SSR && !localStorageListenerAdded) {
      localStorageListenerAdded = true
      window.addEventListener('storage', ({ key, newValue }) => {
        if (key === 'isLoggedIn' && newValue === null) {
          forceLogout()
        }
      })
    }
  }

  const clearUserToken = () => {
    token.value = null
    user.value = null
    profile.value = null

    clearJwtCookie()

    clearInterval(refreshTokenInterval)

    clearInterval(checkCookieInterval)
  }

  const registerUser = async (payload: RegisterPayload) => {
    await register(orcConfig(), payload.email, payload.password, payload.acceptedTermsAndConditions, payload.acceptedDataPrivacyPolicy, pageStore.brandFromStore, 'user', payload.newsletter, payload.captcha)
  }

  const resetPassword = async (payload: { email: string }) => {
    await resetPasswordOrcApi(orcConfig(), payload.email, pageStore.brandFromStore)
  }

  const activateAccount = async ({ oneTimePassword, newsletter }: { oneTimePassword: string, newsletter?: boolean }) => {
    const data = await activate(orcConfig(), oneTimePassword, newsletter, pageStore.brandFromStore)
    if (data.token) {
      setToken(data.token)
      await userInit()
    }
  }

  const deleteUser = () => {
    return deleteUserOrcApi(orcConfig())
  }

  const userInit = async () => {
    try {
      startSessionIntervals()

      if (pageStore.brandFromStore === 'lecker') {
        storeProfile(await getUserProfile(orcConfig()))
      }
    } catch (error: any) {
      console.error('error logging in', error)
      if (error.response?.status) {
        pageStore.setStatus(error.response.status)
      }
    }
  }

  const checkDisplayNameExists = (params: unknown) => {
    return checkDisplayNameExistsOrcApi(orcConfig(), params)
  }

  const changePassword = (params: { oldPassword: string, newPassword: string }) => {
    return changePasswordOrcApi(orcConfig(), params.oldPassword, params.newPassword)
  }

  const changePasswordOtp = (params: { oneTimePassword: string, newPassword: string }) => {
    return changePasswordOtpOrcApi(orcConfig(), params.oneTimePassword, params.newPassword, pageStore.brandFromStore)
  }

  return {
    token,
    user,
    profile,
    loginOverlay,
    isLoggedIn,
    hasPurSub,
    login,
    facebookLogin,
    facebookRegister,
    logout,
    getOneTimePassword,
    forceLogout,
    ensureLogin,
    setToken,
    startSessionIntervals,
    refreshAuthToken,
    checkCookie,
    watchLocalStorage,
    storeToken,
    storeUser,
    storeProfile,
    clearUserToken,
    clearLoginOverlay,
    storeLoginOverlay,
    storeOneTimePassword,
    registerUser,
    resetPassword,
    activateAccount,
    deleteUser,
    userInit,
    checkDisplayNameExists,
    changePassword,
    changePasswordOtp
  }
})
